import { useCallback, useEffect, useRef, useState } from 'react'

import {
  logAIGenerationError,
  normalizeAISuggestion,
  pollForGenerationResult,
} from '@/hooks/services/aiGenerationService'
import type { AIFieldState } from '@/hooks/types/aiFieldGeneration'
import { useGlobalState } from '@/hooks/useGlobalState'
import {
  contentAccessFlags,
  type ContentAccessType,
  resolveContentAccess,
  resolvePrimaryAccess,
} from '@/utils/contentAccess'

import type { CMFormSection } from './ContentModuleFormScreen'
import type { FAQSectionRef } from './FAQSection'
import { useStepResources } from './useStepResources'

type DescriptionAIState = 'idle' | 'loading' | 'pending'

export type ThumbnailDraft = { kind: 'unchanged' } | { kind: 'replaced'; s3Key: string } | { kind: 'removed' }

export function useReviewModalState(props: CMFormSection, opts: { isPrimary: boolean }) {
  const { api } = useGlobalState()
  const { isPrimary } = opts

  const [pendingFileName, setPendingFileName] = useState<string | null>(null)
  // Resolved, never asked: decision 2 for the primary, and the table's per-row control
  // owns it for supplementary files. The modal still holds the value because it decides
  // what the AI, transcript and FAQ sections do, and it writes both flags on save.
  //
  // The effect below owns the real value and overwrites this as soon as the source
  // resource lands. It is the file's format that decides, never the module's delivery
  // mode, so the seed is the common answer rather than a second rule.
  const [contentAccessType, setContentAccessType] = useState<ContentAccessType>('full_access')
  const [pendingDescription, setPendingDescription] = useState<string | null>(null)
  const [thumbnailDraft, setThumbnailDraft] = useState<ThumbnailDraft>({ kind: 'unchanged' })

  const [descriptionAIState, setDescriptionAIState] = useState<DescriptionAIState>('idle')
  // This field runs its own generation rather than going through `useAIFieldGeneration`, so
  // it has to report its own outcome — without one the wrapper can never badge it until a
  // save brings the saved provenance back.
  const [descriptionOutcome, setDescriptionOutcome] = useState<AIFieldState['lastOutcome']>(undefined)
  const [descriptionSuggestion, setDescriptionSuggestion] = useState<string | null>(null)

  const autoGenerationTriggeredRef = useRef<string | null>(null)
  const descriptionAutoGenTriggeredRef = useRef<string | null>(null)
  // One-shot per step. `'ai-applied'` tells the resource-init effect not to blank out
  // the descriptionAi seed when the resource has no description of its own.
  const descriptionSeedRef = useRef<{ stepId: string; source: 'evaluated' | 'ai-applied' } | null>(null)
  const accessTypeInitRef = useRef<string | null>(null)
  const youtubeTitleSeedRef = useRef<string | null>(null)
  const descriptionAbortRef = useRef<AbortController | null>(null)

  // Closing the modal unmounts this, and a description generation that hit a network error
  // is polling for up to six minutes by then. Nothing is left to receive the result, so it
  // is stopped rather than left running against a screen that has gone.
  useEffect(() => {
    return () => {
      descriptionAbortRef.current?.abort()
      descriptionAbortRef.current = null
    }
  }, [])
  const faqSectionRef = useRef<FAQSectionRef>(null)

  const { stepResources, stepResourcesSettled, uploadedFileResource, sourceFileResource } = useStepResources(
    props.stepId,
    props.stepContentQuery.data?.sourceUrl,
  )

  const hasContent = !!props.stepContentQuery.data
  const hasExtractedContent = !!props.stepContentQuery.data?.content?.trim()

  const { showInApp, useInRag } = contentAccessFlags(contentAccessType)

  // Seed description from task_steps.descriptionAi when blank (once per step). Decision 19:
  // the value is applied, not offered. Dead for anything processed since c5e7066b (2026-08-25),
  // which fills the resource's own description too; kept for files processed before it, which
  // were not backfilled and whose resource description the blank test still finds empty.
  useEffect(() => {
    if (!props.stepId || !props.stepsQuery?.data) return
    if (stepResources === undefined) return
    if (descriptionSeedRef.current?.stepId === props.stepId) return

    const step = props.stepsQuery.data.find(s => s.stepId === props.stepId)
    if (!step?.descriptionAi?.trim()) return

    const descriptionBlank = !(pendingDescription?.trim() || sourceFileResource?.description?.trim())
    if (!descriptionBlank || descriptionAIState !== 'idle') {
      // Mark evaluated even on skip — keeps this effect one-shot per step.
      descriptionSeedRef.current = { stepId: props.stepId, source: 'evaluated' }
      return
    }

    setPendingDescription(step.descriptionAi.trim())
    // Same reason as the title seed: without an outcome the tag never appears, and a legacy
    // file would show one tagged field above one untagged. Decision 30 keeps the two together.
    setDescriptionOutcome('succeeded')
    descriptionSeedRef.current = { stepId: props.stepId, source: 'ai-applied' }
  }, [
    props.stepId,
    props.stepsQuery?.data,
    pendingDescription,
    sourceFileResource?.description,
    descriptionAIState,
    stepResources,
  ])

  // Initialize description from the source file resource. Skip when the seed
  // ref shows we already applied a descriptionAi value (media mode).
  useEffect(() => {
    if (!sourceFileResource) return
    const resourceDesc = sourceFileResource.description?.trim() ?? ''
    const seed = descriptionSeedRef.current
    const aiSeededHere = seed != null && seed.stepId === props.stepId && seed.source === 'ai-applied'
    if (aiSeededHere && !resourceDesc) return
    setPendingDescription(sourceFileResource.description ?? '')
  }, [sourceFileResource?.resourceId, sourceFileResource?.description, props.stepId])

  // Track the saved value so the modal writes back what the table set, rather than the
  // default this state was created with. The primary resolves instead of reading, so a
  // stored Media Only from before decision 2 is corrected on the next save; a stored
  // Information Only is the expert's own choice and is carried through.
  useEffect(() => {
    if (!sourceFileResource) return

    const facts = { type: sourceFileResource.type, showInAppSupported: sourceFileResource.showInAppSupported }

    if (isPrimary) {
      setContentAccessType(resolvePrimaryAccess({ ...facts, showInApp: sourceFileResource.showInApp }))
      return
    }

    const resourceKey = `${sourceFileResource.resourceId}-${sourceFileResource.showInApp}-${sourceFileResource.showInAppSupported}`
    if (accessTypeInitRef.current === resourceKey) return
    accessTypeInitRef.current = resourceKey

    const stepData = props.stepsQuery.data?.find(s => s.stepId === props.stepId)
    setContentAccessType(
      resolveContentAccess({
        ...facts,
        showInApp: sourceFileResource.showInApp,
        useInRag: stepData?.useInRag ?? true,
      }),
    )
  }, [
    sourceFileResource?.resourceId,
    sourceFileResource?.type,
    sourceFileResource?.showInApp,
    sourceFileResource?.showInAppSupported,
    props.stepsQuery.data,
    props.stepId,
    isPrimary,
  ])

  async function handleDescriptionNetworkError(abortController: AbortController, stepId: string) {
    const pollResult = await pollForGenerationResult(
      api,
      { entityType: 'step', entityId: stepId, systemPromptTemplateId: 'generate_cm_file_description' },
      { signal: abortController.signal },
    )
    if (pollResult.status === 'done') {
      const suggestion = normalizeAISuggestion(pollResult.result)
      setDescriptionSuggestion(suggestion)
      setDescriptionAIState('pending')
      setDescriptionOutcome('succeeded')
    } else if (pollResult.status === 'aborted') {
      // Cancelled by user — state already reset
    } else {
      const reason = pollResult.status === 'failed' ? pollResult.error : 'Polling timed out'
      logAIGenerationError(reason, null)
      setDescriptionAIState('idle')
      setDescriptionOutcome('failed')
    }
  }

  async function generateSourceFileDescription() {
    if (!props.stepId) return

    const abortController = new AbortController()
    descriptionAbortRef.current = abortController
    setDescriptionAIState('loading')
    setDescriptionOutcome(undefined)

    try {
      const response = await api.safeRequest(api.generate, {
        step_id: props.stepId,
        system_prompt_template_id: 'generate_cm_file_description',
        message_template_id: 'generate_step_message',
        extra_data: {
          accepted_data: pendingDescription || undefined,
        },
      })

      if (abortController.signal.aborted) return

      if (response?.ok && response.data) {
        const suggestion = normalizeAISuggestion(response.data as string)
        setDescriptionSuggestion(suggestion)
        setDescriptionAIState('pending')
        setDescriptionOutcome('succeeded')
      } else if (!response.ok && response.isNetworkError) {
        await handleDescriptionNetworkError(abortController, props.stepId)
      } else {
        setDescriptionAIState('idle')
        setDescriptionOutcome('failed')
      }
    } catch {
      setDescriptionAIState('idle')
      setDescriptionOutcome('failed')
    } finally {
      if (descriptionAbortRef.current === abortController) {
        descriptionAbortRef.current = null
      }
    }
  }

  async function generateSourceFileDescriptionWithGuide(instructions: string) {
    if (!props.stepId) return

    const abortController = new AbortController()
    descriptionAbortRef.current = abortController
    setDescriptionAIState('loading')
    setDescriptionOutcome(undefined)

    try {
      const extraData: Record<string, string | undefined> = {
        accepted_data: pendingDescription || undefined,
        user_instructions: instructions.trim() || undefined,
      }
      if (descriptionAIState === 'pending' && descriptionSuggestion) {
        extraData.proposed_data = descriptionSuggestion
      }

      const response = await api.safeRequest(api.generate, {
        step_id: props.stepId,
        system_prompt_template_id: 'generate_cm_file_description',
        message_template_id: 'generate_step_message',
        extra_data: extraData,
      })

      if (abortController.signal.aborted) return

      if (response?.ok && response.data) {
        const suggestion = normalizeAISuggestion(response.data as string)
        setDescriptionSuggestion(suggestion)
        setDescriptionAIState('pending')
        setDescriptionOutcome('succeeded')
      } else if (!response.ok && response.isNetworkError) {
        await handleDescriptionNetworkError(abortController, props.stepId)
      } else {
        setDescriptionAIState('idle')
        setDescriptionOutcome('failed')
      }
    } catch {
      setDescriptionAIState('idle')
      setDescriptionOutcome('failed')
    } finally {
      if (descriptionAbortRef.current === abortController) {
        descriptionAbortRef.current = null
      }
    }
  }

  function acceptDescriptionSuggestion() {
    if (descriptionAIState !== 'pending') return
    // A response that normalises to nothing has nothing to accept. Reset rather than
    // return, or the field rests at `pending` forever behind a disabled input, and the
    // auto-accept effect bails on the same empty suggestion. Matches `acceptSuggestion`.
    if (!descriptionSuggestion) {
      setDescriptionSuggestion(null)
      setDescriptionAIState('idle')
      setDescriptionOutcome('failed')
      return
    }

    setPendingDescription(descriptionSuggestion)
    setDescriptionSuggestion(null)
    setDescriptionAIState('idle')
  }

  function rejectDescriptionSuggestion() {
    setDescriptionSuggestion(null)
    setDescriptionAIState('idle')
    setDescriptionOutcome(undefined)
  }

  function cancelDescriptionGeneration() {
    descriptionAbortRef.current?.abort()
    descriptionAbortRef.current = null
    setDescriptionSuggestion(null)
    setDescriptionAIState('idle')
    setDescriptionOutcome(undefined)
  }

  const isMutating = props.stepUpdateMutation.isPending

  const markThumbnailReplaced = useCallback((s3Key: string) => {
    setThumbnailDraft({ kind: 'replaced', s3Key })
  }, [])

  const markThumbnailRemoved = useCallback(() => {
    setThumbnailDraft({ kind: 'removed' })
  }, [])

  const clearThumbnailDraft = useCallback(() => {
    setThumbnailDraft({ kind: 'unchanged' })
  }, [])

  // Drop pending thumbnail state when the step changes so a pending upload
  // from one step can't leak into another if the modal instance is reused.
  useEffect(() => {
    setThumbnailDraft({ kind: 'unchanged' })
  }, [props.stepId])

  return {
    stepResources,
    stepResourcesSettled,

    pendingFileName,
    setPendingFileName,
    contentAccessType,
    showInApp,
    useInRag,
    pendingDescription,
    setPendingDescription,
    thumbnailDraft,
    markThumbnailReplaced,
    markThumbnailRemoved,
    clearThumbnailDraft,

    descriptionAIState,
    descriptionOutcome,
    descriptionSuggestion,
    generateSourceFileDescription,
    generateSourceFileDescriptionWithGuide,
    acceptDescriptionSuggestion,
    rejectDescriptionSuggestion,
    cancelDescriptionGeneration,

    autoGenerationTriggeredRef,
    descriptionAutoGenTriggeredRef,
    youtubeTitleSeedRef,
    faqSectionRef,

    hasContent,
    hasExtractedContent,
    isMutating,
    uploadedFileResource,
    sourceFileResource,
  }
}
